import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Loading } from "./components/States";
import Login from "./pages/Login";
import Homepage from "./pages/Homepage";
import SmartQuote from "./pages/SmartQuote";
import PayDeposit from "./pages/PayDeposit";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import NotFound from './pages/NotFound';
import ServiceDetail from './pages/ServiceDetail';
import Services from './pages/Services';
import PublicProjects from './pages/PublicProjects';
import Contact from './pages/Contact';
import Resources from './pages/Resources';
import Marketplace from './pages/Marketplace';
import Residential from './pages/Residential';
import Commercial from './pages/Commercial';
import Builders from './pages/Builders';
import ServiceAreas from './pages/ServiceAreas';
import TownPage from './pages/TownPage';
import StartYourCompany from './pages/StartYourCompany';
import AccountAccess from './pages/AccountAccess';
import { PageMetadata, PrivacyPreferences } from './components/PageMetadata';
import ClientWorkspace from './pages/ClientWorkspace';
import OfficeDesk from './pages/owner/OfficeDesk';
import OwnerQuotes from "./pages/owner/OwnerQuotes";

import { TopShell } from "./layouts/TopShell";
import { FieldShell } from "./layouts/FieldShell";
import { CustomerShell } from "./layouts/CustomerShell";

import CommandCenter from "./pages/owner/CommandCenter";
import Sales from "./pages/owner/Sales";
import Projects from "./pages/owner/Projects";
import LotDetail from "./pages/owner/LotDetail";
import Service from "./pages/owner/Service";
import Operations from "./pages/owner/Operations";
import Money from "./pages/owner/Money";
import Reports from "./pages/owner/Reports";

import BuilderDashboard from "./pages/builder/BuilderDashboard";
import BuilderCommunities from "./pages/builder/BuilderCommunities";
import BuilderChangeOrders from "./pages/builder/BuilderChangeOrders";
import BuilderBilling from "./pages/builder/BuilderBilling";
import BuilderDocuments from "./pages/builder/BuilderDocuments";

import FieldToday from "./pages/field/FieldToday";
import FieldSchedule from "./pages/field/FieldSchedule";
import FieldJobs from "./pages/field/FieldJobs";
import FieldJobDetail from "./pages/field/FieldJobDetail";
import FieldTime from "./pages/field/FieldTime";
import FieldProfile from "./pages/field/FieldProfile";

import CustomerHome from "./pages/customer/CustomerHome";
import CustomerAppointments from "./pages/customer/CustomerAppointments";
import CustomerEstimates from "./pages/customer/CustomerEstimates";
import CustomerProjects from "./pages/customer/CustomerProjects";
import ProjectTracking from "./pages/customer/ProjectTracking";
import CustomerPayments from "./pages/customer/CustomerPayments";
import CustomerWarranty from "./pages/customer/CustomerWarranty";

const OWNER_NAV = [
  { to: "", label: "Home" },
  { to: "sales", label: "Sales" },
  { to: "quotes", label: "Smart Quotes" },
  { to: "projects", label: "Projects" },
  { to: "service", label: "Service" },
  { to: "operations", label: "Operations" },
  { to: "money", label: "Money" },
  { to: "desk", label: "Office Desk" },
  { to: "reports", label: "Reports" },
];
const BUILDER_NAV = [
  { to: "", label: "Dashboard" },
  { to: "communities", label: "Communities" },
  { to: "change-orders", label: "Change Orders" },
  { to: "billing", label: "Billing" },
  { to: "requests", label: "Bids & Project Desk" },
  { to: "documents", label: "Documents" },
];

const HOME = { owner: "/app", office: "/app/operations", builder: "/builder", field: "/field", homeowner: "/portal" };

function Guard({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading label="Starting Hardwire OS" />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={HOME[user.role] || "/login"} replace />;
  return children;
}

function Root() {
  const { user, loading } = useAuth();
  if (loading) return <Loading label="Starting Hardwire OS" />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={HOME[user.role] || "/login"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <Toaster theme="dark" position="top-right" richColors />
      <BrowserRouter>
        <PageMetadata /><PrivacyPreferences />
        <Routes>
          <Route path="/services/:service" element={<ServiceDetail />} />
          <Route path="/services" element={<Services />} />
          <Route path="/projects" element={<PublicProjects />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/residential" element={<Residential />} />
          <Route path="/commercial" element={<Commercial />} />
          <Route path="/builders" element={<Builders />} />
          <Route path="/service-areas" element={<ServiceAreas />} />
          <Route path="/service-areas/:town" element={<TownPage />} />
          <Route path="/start-an-electrical-company" element={<StartYourCompany />} />
          <Route path="/register" element={<AccountAccess />} />
          <Route path="/forgot-password" element={<AccountAccess mode="forgot" />} />
          <Route path="/reset-password" element={<AccountAccess mode="reset" />} />
          <Route path="/" element={<Homepage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/quote" element={<SmartQuote />} />
          <Route path="/pay/:token" element={<PayDeposit />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />

          {/* Internal: owner + office */}
          <Route element={<Guard roles={["owner", "office"]}><TopShell nav={OWNER_NAV} base="/app" title="Command Center" /></Guard>}>
            <Route path="/app" element={<CommandCenter />} />
            <Route path="/app/sales" element={<Sales />} />
            <Route path="/app/quotes" element={<OwnerQuotes />} />
            <Route path="/app/projects" element={<Projects />} />
            <Route path="/app/projects/lot/:id" element={<LotDetail />} />
            <Route path="/app/service" element={<Service />} />
            <Route path="/app/operations" element={<Operations />} />
            <Route path="/app/money" element={<Money />} />
            <Route path="/app/desk" element={<OfficeDesk />} />
            <Route path="/app/reports" element={<Reports />} />
          </Route>

          {/* Builder */}
          <Route element={<Guard roles={["builder"]}><TopShell nav={BUILDER_NAV} base="/builder" title="Builder Portal" /></Guard>}>
            <Route path="/builder" element={<BuilderDashboard />} />
            <Route path="/builder/communities" element={<BuilderCommunities />} />
            <Route path="/builder/communities/lot/:id" element={<LotDetail />} />
            <Route path="/builder/change-orders" element={<BuilderChangeOrders />} />
            <Route path="/builder/billing" element={<BuilderBilling />} />
            <Route path="/builder/requests" element={<ClientWorkspace />} />
            <Route path="/builder/documents" element={<BuilderDocuments />} />
          </Route>

          {/* Field */}
          <Route element={<Guard roles={["field"]}><FieldShell /></Guard>}>
            <Route path="/field" element={<FieldToday />} />
            <Route path="/field/schedule" element={<FieldSchedule />} />
            <Route path="/field/jobs" element={<FieldJobs />} />
            <Route path="/field/jobs/:id" element={<FieldJobDetail />} />
            <Route path="/field/time" element={<FieldTime />} />
            <Route path="/field/profile" element={<FieldProfile />} />
          </Route>

          {/* Homeowner */}
          <Route element={<Guard roles={["homeowner"]}><CustomerShell /></Guard>}>
            <Route path="/portal" element={<CustomerHome />} />
            <Route path="/portal/appointments" element={<CustomerAppointments />} />
            <Route path="/portal/estimates" element={<CustomerEstimates />} />
            <Route path="/portal/projects" element={<CustomerProjects />} />
            <Route path="/portal/projects/:id" element={<ProjectTracking />} />
            <Route path="/portal/payments" element={<CustomerPayments />} />
            <Route path="/portal/requests" element={<ClientWorkspace />} />
            <Route path="/portal/warranty" element={<CustomerWarranty />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
